import React, { useState, useEffect } from 'react';
import componentStyles from './home.module.css'; // Import the CSS file for styling
import globalStyles from '../globals.module.css';
import Search from '../search/search';
import Settings from '../settings/settings';
import Profile from '../profile/profile';
import Login from '../login/login';
import Register from '../login/register';
import PasswordResetRequest from '../passwordReset/passwordResetRequest';
import eventBus from '../../service/eventBus';
import InDepthBetCard from '../betFeed/inDepthBetCard';
import ParlayGenerator from '../parlayGenerator/parlayGenerator';
import BetCartButton from '../betCart/betCartButton';
import BetCart from '../betCart/betCart';
import SnipedOdds from '../snipedOdds/snipedOdds';
import PropsViewer from '../propsViewer/propsViewer';
import Models from '../models/models';
import Research from '../research/research';
import { EVENT_UPDATE_ACTION_BAR_VIEW, EVENT_UPDATE_AUTHENTICATED } from '../../utils/constants';
import Landing from '../landing/landing';
import SessionService from '../../service/sessionService';
import { authenticateUser } from '../../service/userService';
import AnalyticsService from '../../service/analyticsService';
import { refreshSubscriptionState } from '../../service/billingService';
import { clearStaleAuthentication } from '../../service/httpClient';

const scrollPageToTop = () => {
    window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        document.querySelectorAll(`.${globalStyles.viewContainer}, .${globalStyles.pageContainer}`).forEach((element) => {
            element.scrollTop = 0;
            element.scrollLeft = 0;
        });
    });
};

const Home = () => {
    const componentId = 'Home';
    const hasStoredSession = SessionService.getInstance().hasSessionToken();
    const [currentView, setCurrentView] = useState('snipedOdds');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(hasStoredSession);
    const [viewMap, setViewMap] = useState({});

    const handleViewChange = (view) => {
        const nextView = view === 'feed' ? 'props' : view;
        console.log(`Home: Event ${EVENT_UPDATE_ACTION_BAR_VIEW} received with value`, nextView);
        AnalyticsService.getInstance().setViewContext(nextView, currentView);
        setCurrentView(nextView);
        scrollPageToTop();
    }

    const handleAuthenticated = (isAuthenticated) => {
        console.log(`Home: Event ${EVENT_UPDATE_AUTHENTICATED} received with value`, isAuthenticated);
        if(isAuthenticated) {
            AnalyticsService.getInstance().setViewContext('snipedOdds', currentView);
            setIsAuthenticated(true);
            setCurrentView('snipedOdds');
            scrollPageToTop();
        }
        else {
            AnalyticsService.getInstance().setViewContext('snipedOdds', currentView);
            setIsAuthenticated(false);
            setCurrentView('snipedOdds');
            scrollPageToTop();
        }
    }

    useEffect(() => {
        scrollPageToTop();
    }, [currentView]);

    useEffect(() => {
        let isCancelled = false;

        // Check for password reset success flag
        const isResetSuccess = sessionStorage.getItem('passwordResetSuccess');
        if (isResetSuccess) {
            sessionStorage.removeItem('passwordResetSuccess');
            setCurrentView('login');
        }

        // Setup event listeners
        eventBus.on(componentId, EVENT_UPDATE_ACTION_BAR_VIEW, handleViewChange);
        eventBus.on(componentId, EVENT_UPDATE_AUTHENTICATED, handleAuthenticated);

        const validateIsAuthenticated = async () => {
            if (isCancelled) return;

            const hasSessionToken = SessionService.getInstance().hasSessionToken();
            if (hasSessionToken) {
                const sessionId = SessionService.getInstance().getSessionId();
                try {
                    const response = await authenticateUser(sessionId);
                    if (isCancelled) return;
                    const { data } = response;
                    if (data.success) {
                        SessionService.getInstance().applyAuthResults(data.results);
                        try {
                            await refreshSubscriptionState();
                        } catch (error) {
                            console.error('Error fetching subscription during auth:', error);
                        }
                        if (isCancelled) return;
                        setIsAuthenticated(true);
                        eventBus.emit(EVENT_UPDATE_AUTHENTICATED, true);
                        eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'snipedOdds');
                        setCurrentView('snipedOdds');
                    } else {
                        clearStaleAuthentication();
                        console.error(data.error);
                    }
                } catch (error) {
                    clearStaleAuthentication();
                    console.error('Authentication error:', error);
                }
            }
            if (!isCancelled) {
                setIsLoading(false);
            }
        }
        
        const authTimerId = window.setTimeout(validateIsAuthenticated, 1000);
        
        // Cleanup event listener on component unmount
        return () => {
            isCancelled = true;
            window.clearTimeout(authTimerId);
            eventBus.off(componentId, EVENT_UPDATE_ACTION_BAR_VIEW);
            eventBus.off(componentId, EVENT_UPDATE_AUTHENTICATED);
        };
    }, []);

    const renderLoadingSpinner = () => {
        return (
            <div className={globalStyles.loadingContainer}>
                <div className={globalStyles.spinner}></div>
                <p>Verifying authentication...</p>
            </div>
        );
    };

    const renderUnauthenticatedView = () => {
        switch(currentView) {
            case 'snipedOdds':
                return (
                    <div className={globalStyles.viewContainer}>
                        <SnipedOdds />
                    </div>
                );
            case 'models':
                return (
                    <div className={globalStyles.viewContainer}>
                        <Models />
                    </div>
                );
            case 'research':
                return (
                    <div className={globalStyles.viewContainer}>
                        <Research />
                    </div>
                );
            case 'props':
                return (
                    <div className={globalStyles.viewContainer}>
                        <PropsViewer />
                    </div>
                );
            case 'landing':
                return (
                    <div className={globalStyles.viewContainer}>
                        <Landing />
                    </div>
                );
            case 'login':
                return (
                    <div className={globalStyles.viewContainer}>
                        <Login />
                    </div>
                );
            case 'register':
                return (
                    <div className={globalStyles.viewContainer}>
                        <Register />
                    </div>
                );
            case 'reset-password':
                return (
                    <div className={globalStyles.viewContainer}>
                        <PasswordResetRequest />
                    </div>
                );
            default:
                return (
                    <div className={globalStyles.viewContainer}>
                        <SnipedOdds />
                    </div>
                );
        }
    }

    const renderAuthenticatedView = () => {
        // Render the appropriate component based on currentView
        switch (currentView) {
            case 'feed':
                return (
                    <div className={globalStyles.viewContainer}>
                        <PropsViewer />
                    </div>
                );
            case 'search':
                if(Object.keys(viewMap).includes('search')) {
                    return viewMap['search'];
                }
                else {
                    let prevViewMap = {...viewMap};
                    let searchView = (
                        <div className={globalStyles.viewContainer}>
                            <Search />
                        </div>
                    );
                    prevViewMap['search'] = searchView;
                    setViewMap(prevViewMap);
                    return searchView;
                }
            case 'settings':
                return (
                    <div className={globalStyles.viewContainer}>
                        <Settings />
                    </div>
                );
            case 'profile':
                return (
                    <div className={globalStyles.viewContainer}>
                        <Profile />
                    </div>
                );
            case 'parlay':
                return (
                    <div className={globalStyles.viewContainer}>
                        <ParlayGenerator />
                    </div>
                );
            case 'snipedOdds':
                return (
                    <div className={globalStyles.viewContainer}>
                        <SnipedOdds />
                    </div>
                );
            case 'models':
                return (
                    <div className={globalStyles.viewContainer}>
                        <Models />
                    </div>
                );
            case 'research':
                return (
                    <div className={globalStyles.viewContainer}>
                        <Research />
                    </div>
                );
            case 'props':
                return (
                    <div className={globalStyles.viewContainer}>
                        <PropsViewer />
                    </div>
                );
            case 'inDepthBetCard':
                return (
                    <div className={globalStyles.viewContainer}>
                        <InDepthBetCard />
                    </div>
                );
            default:
                return (
                    <div className={globalStyles.viewContainer}>
                        <SnipedOdds />
                    </div>
                );
        }
    }

    if (isLoading) {
        return renderLoadingSpinner();
    }

    return (
        <>
            {isAuthenticated ? renderAuthenticatedView() : renderUnauthenticatedView()}
            {isAuthenticated && <BetCartButton />}
            {isAuthenticated && <BetCart />}
        </>
    );
};

export default Home; 
